import { ANY_AIB_REGISTRY_SENTINEL } from "@/constants";
import {
  DEFAULT_CANCELLATION_PURPOSE,
  getRegistryRule,
  isPriorYearPeriodAllowed,
  resolveEffectiveRegion,
  supportsPrivateCancellation,
} from "@/dashboard/inventory_management/common/cancellationRegistryRules";
import {
  BeneficiaryTypeEnum,
  type GrexelStatementOptions,
  type IndividualCancelStatement,
} from "@/types";
import { getRegistryLabel, type TranslationFn } from "@/utils/registries";

export { getDefaultPeriodRange as getDefaultCancelPeriodRange } from "@/dashboard/inventory_management/common/useDateUtils";

export const CANCEL_COMPANY_PLACEHOLDER = "{beneficiary_name}";
export const CANCEL_ADDRESS_PLACEHOLDER = "{address}";
export const MAX_CANCEL_STATEMENTS = 1000;

/** Whether a purpose template references the per-row {address} placeholder. */
export const templateUsesAddressPlaceholder = (
  template: string | null | undefined
): boolean => !!template && template.includes(CANCEL_ADDRESS_PLACEHOLDER);

/** Whether an editor row's selected registry is the "Any AIB" sentinel (DEV-576). */
export const isAnyAibRow = (
  registryCountryRegion: string | null | undefined
): boolean => registryCountryRegion === ANY_AIB_REGISTRY_SENTINEL;

/**
 * Label for a region as the cancel form offers it: any supported registry, plus the "Any AIB"
 * sentinel (DEV-576). The sentinel is deliberately kept out of the global
 * SUPPORTED_REGISTRIES_WITH_LABELS list — it is an editor-only value that materializes to "NO" with
 * is_any_aib=true on submit — so `getRegistryLabel` alone would surface it as the raw "ANY_AIB" code.
 */
export const getCancelRegionLabel = (
  registryCountryRegion: string | null | undefined,
  t: TranslationFn
): string =>
  isAnyAibRow(registryCountryRegion)
    ? t("go_cancel.any_aib_option")
    : getRegistryLabel(registryCountryRegion, t);

/**
 * Reads a statement's privacy state (DEV-583). Absent options mean the registry default, which is a
 * public statement — so this is the one place that spells out the `is_public_cancellation === false`
 * double negative. Counterpart to {@link withStatementPrivacy}.
 */
export const isStatementPrivate = (
  options: GrexelStatementOptions | null | undefined
): boolean => options?.is_public_cancellation === false;

/**
 * Builds the registry options for a statement's privacy choice (DEV-583) — the single writer, so the
 * polarity is inverted in exactly one place. Public is expressed by omitting the object entirely
 * rather than sending `true`, matching the backend default.
 */
export const withStatementPrivacy = (
  keepPrivate: boolean | undefined
): GrexelStatementOptions | undefined =>
  keepPrivate ? { is_public_cancellation: false } : undefined;

export interface CancelSharedDefaults {
  beneficiary_type: BeneficiaryTypeEnum;
  period_start: string;
  period_end: string;
  cancellation_purpose_template: string;
}

export interface CancelRowInput {
  temp_id?: string;
  beneficiary_name: string;
  registry_country_region?: string;
  registry_code?: string | null;
  address?: string | null;
  mwh_cancelled: number | null;
  keep_statement_private?: boolean;
}

export const getDefaultOperationNotesTemplate = (
  beneficiaryType: BeneficiaryTypeEnum
): string => {
  if (beneficiaryType === BeneficiaryTypeEnum.ELECTRICITY_SELLER) {
    return DEFAULT_CANCELLATION_PURPOSE.ELECTRICITY_SELLER(
      CANCEL_COMPANY_PLACEHOLDER
    );
  }
  return DEFAULT_CANCELLATION_PURPOSE.CONSUMER(CANCEL_COMPANY_PLACEHOLDER);
};

export const sanitizeOperationNotesTemplate = (
  template: string | null | undefined
): string => {
  if (!template) {
    return "";
  }
  return template.trim().replace(/\s+/g, " ");
};

/**
 * Template to use after a beneficiary-type change: swaps in the new type's
 * default while the current value is empty or still the old type's default
 * (i.e. the user hasn't customised it); otherwise keeps the user's text.
 */
export const refreshOperationNotesTemplate = (
  current: string,
  newType: BeneficiaryTypeEnum,
  oldType: BeneficiaryTypeEnum | undefined
): string => {
  const sanitized = sanitizeOperationNotesTemplate(current);
  const oldDefault = oldType ? getDefaultOperationNotesTemplate(oldType) : "";
  if (!sanitized || sanitized === oldDefault) {
    return getDefaultOperationNotesTemplate(newType);
  }
  return current;
};

export const materializeOperationNotesTemplate = (
  template: string,
  beneficiaryName: string,
  address?: string | null
): string => {
  const normalizedTemplate = sanitizeOperationNotesTemplate(template);
  const safeBeneficiaryName =
    beneficiaryName.trim() || CANCEL_COMPANY_PLACEHOLDER;
  // {address} behaves exactly like {beneficiary_name}: when the row's address is empty/null the
  // token is left verbatim (substituting to empty would silently drop it). DEV-576.
  const safeAddress = address?.trim() || CANCEL_ADDRESS_PLACEHOLDER;
  return normalizedTemplate
    .replaceAll(CANCEL_COMPANY_PLACEHOLDER, safeBeneficiaryName)
    .replaceAll(CANCEL_ADDRESS_PLACEHOLDER, safeAddress);
};

export interface CancelValidationOptions {
  today?: Date;
}

export const isCancelPeriodRangeValid = (
  periodStart: string,
  periodEnd: string,
  options: CancelValidationOptions = {}
): boolean => {
  if (!periodStart || !periodEnd) {
    return false;
  }
  if (new Date(periodEnd) < new Date(periodStart)) {
    return false;
  }
  const today = options.today ?? new Date();
  return (
    isPriorYearPeriodAllowed(today, periodStart) &&
    isPriorYearPeriodAllowed(today, periodEnd)
  );
};

export const isCancelSharedDefaultsValid = (
  defaults: CancelSharedDefaults,
  options: CancelValidationOptions = {}
): boolean => {
  return (
    isCancelPeriodRangeValid(
      defaults.period_start,
      defaults.period_end,
      options
    ) &&
    !!sanitizeOperationNotesTemplate(defaults.cancellation_purpose_template)
  );
};

export const isCancelRowMwhValid = (
  mwhCancelled: CancelRowInput["mwh_cancelled"]
): boolean => {
  if (mwhCancelled === null) {
    return false;
  }

  const normalizedMwh = Number(mwhCancelled);
  return Number.isInteger(normalizedMwh) && normalizedMwh > 0;
};

export const isCancelRowValid = (row: CancelRowInput): boolean => {
  const anyAib = isAnyAibRow(row.registry_country_region);
  if (!row.beneficiary_name.trim() || !row.registry_country_region?.trim()) {
    return false;
  }
  // Registry code is required for normal rows; for Any AIB it is optional (DEV-576).
  if (!anyAib && !row.registry_code?.trim()) {
    return false;
  }
  if (!isCancelRowMwhValid(row.mwh_cancelled)) {
    return false;
  }
  // getRegistryRule resolves the Any AIB sentinel to Norway, which requires an address.
  const requiresAddress = getRegistryRule(
    row.registry_country_region
  ).requiresAddress;
  if (requiresAddress && !row.address?.trim()) {
    return false;
  }
  return true;
};

export const buildCancelStatementPayload = (
  row: CancelRowInput,
  sharedDefaults: CancelSharedDefaults
): IndividualCancelStatement => {
  // DEV-576: the "Any AIB" sentinel materializes to a Norway cancellation with is_any_aib=true.
  // The region submitted is always "NO" so routing, the supported-registry gate, execution and
  // backend address validation all run unchanged; only the beneficiary location differs (backend).
  const anyAib = isAnyAibRow(row.registry_country_region);
  const trimmedAddress = row.address?.trim() || null;
  const effectiveRegion = resolveEffectiveRegion(row.registry_country_region);
  return {
    temp_id: row.temp_id,
    beneficiary_name: row.beneficiary_name.trim(),
    beneficiary_type: sharedDefaults.beneficiary_type,
    registry_code: row.registry_code?.trim() ?? null,
    registry_country_region: effectiveRegion,
    address: trimmedAddress,
    period_start: sharedDefaults.period_start,
    period_end: sharedDefaults.period_end,
    mwh_cancelled:
      row.mwh_cancelled === null ? null : Math.floor(Number(row.mwh_cancelled)),
    cancellation_purpose: materializeOperationNotesTemplate(
      sharedDefaults.cancellation_purpose_template,
      row.beneficiary_name,
      trimmedAddress
    ),
    is_any_aib: anyAib,
    // DEV-583: region-gated so switching away from a Grexel registry drops the flag declaratively.
    grexel_options: supportsPrivateCancellation(effectiveRegion)
      ? withStatementPrivacy(row.keep_statement_private)
      : undefined,
  };
};

const getSingleSelectedRegion = (
  selectedRegions: string[] | undefined
): string | null => {
  if (!selectedRegions || selectedRegions.length !== 1) {
    return null;
  }
  return selectedRegions[0] ?? null;
};

export const resolveDefaultRegistryRegion = (
  currentRegistryRegion: string | null | undefined,
  selectedRegions: string[] | undefined
): string | null => {
  if (currentRegistryRegion?.trim()) {
    return null;
  }
  return getSingleSelectedRegion(selectedRegions);
};
